import { LinkButton } from "@/ui/next/LinkButton";
import { useTranslation } from "next-i18next";
import { useRouter } from "next/router";

import { IconStateAlertRound } from "@/assets/icons/figma/IconStateAlertRound";
import { IconStateClockTime } from "@/assets/icons/figma/IconStateClockTime";
import { CREATE_AD_V2_ROUTE } from "@/features/create-ad-v2/static/create-ad-v2.static";
import { useSellRoute } from "@/features/us-market/hooks/useSellRoute";
import type {
  ValueCheckFailure,
  ValueCheckFailureReason,
} from "@/features/value-check/model";
import { InfoPanel } from "@/ui/InfoPanel";
import { Button } from "@/ui/cva/Button";
import { Heading } from "@/ui/cva/Heading";
import { Body } from "@/ui/typography/Body";

const MS_PER_SECOND = 1000;
const SECONDS_PER_HOUR = 3600;

/** A limit: nothing is wrong, the visitor waits (curious). Anything else went wrong (attention). */
const isLimit = (reason: ValueCheckFailureReason) =>
  reason === "limit" || reason === "busy";

/**
 * A check that could not run (WHO-6534 F32): the page title stays, the cause sits in an Info
 * panel under it, and there is always a second route, an ad without the check.
 */
export const ValueCheckFailureView = ({
  failure,
  onTryAgain,
}: {
  failure: ValueCheckFailure;
  onTryAgain: () => void;
}) => {
  const { t } = useTranslation();
  const { locale } = useRouter();
  const { href: createAdHref } = useSellRoute(
    "value_check",
    CREATE_AD_V2_ROUTE
  );

  const retryAfter =
    failure.reason === "limit" ? failure.retryAfterSeconds ?? null : null;
  const retryAt = retryAfter
    ? new Intl.DateTimeFormat(locale, {
        hour: "2-digit",
        minute: "2-digit",
      }).format(new Date(Date.now() + retryAfter * MS_PER_SECOND))
    : null;

  const titles: Record<ValueCheckFailureReason, string> = {
    // The hourly allowance is 5; a longer wait is the daily one.
    limit:
      retryAfter && retryAfter <= SECONDS_PER_HOUR
        ? t(
            "value-check:failed-limit-hour-title",
            "You've done 5 checks this hour"
          )
        : t(
            "value-check:failed-limit-day-title",
            "You've done a lot of checks today"
          ),
    busy: t("value-check:failed-busy-title", "It's very busy right now"),
    challenge: t("value-check:failed-title", "That didn't work"),
    photo: t("value-check:failed-title", "That didn't work"),
    unavailable: t("value-check:failed-title", "That didn't work"),
  };

  const messages: Record<ValueCheckFailureReason, string> = {
    limit: retryAt
      ? t(
          "value-check:failed-limit-at",
          "You can check a photo again at {{time}}. Creating an ad works right away.",
          { time: retryAt }
        )
      : t(
          "value-check:failed-limit-later",
          "You can check a photo again in a while. Creating an ad works right away."
        ),
    busy: t("value-check:failed-busy", "Try again tomorrow."),
    challenge: t(
      "value-check:failed-challenge",
      "The security check didn't work. Reload the page and try again."
    ),
    photo: t(
      "value-check:failed-photo",
      "We couldn't open this photo. Try another photo, for example a JPG or PNG."
    ),
    unavailable: t(
      "value-check:failed-unavailable",
      "Something went wrong on our side. Try again in a few minutes."
    ),
  };

  const limit = isLimit(failure.reason);
  const withoutCheck = t(
    "value-check:create-ad-without-check",
    "Create ad without value check"
  );

  return (
    <section
      aria-labelledby="value-check-failed-title"
      className="grid max-w-[560px] gap-6 py-4 md:py-10"
      data-testid="value-check.failed"
    >
      <Heading as="h1" size="h1" id="value-check-failed-title">
        {t("value-check:title", "What is your item worth?")}
      </Heading>
      <div role="alert">
        <InfoPanel
          className="md:max-w-96"
          color={limit ? "curious" : "attention"}
          icon={
            limit ? (
              <IconStateClockTime aria-hidden className="size-6 shrink-0" />
            ) : (
              <IconStateAlertRound aria-hidden className="size-6 shrink-0" />
            )
          }
          body={
            <div>
              <Heading as="h2" size="h5">
                {titles[failure.reason]}
              </Heading>
              <Body className="leading-5 md:leading-6">
                {messages[failure.reason]}
              </Body>
            </div>
          }
        />
      </div>
      {limit ? (
        <LinkButton
          href={createAdHref}
          color="brand"
          size="medium"
          className="justify-self-start rounded border-0 text-center"
          data-testid="value-check.create-ad-without-check"
        >
          {withoutCheck}
        </LinkButton>
      ) : (
        <div className="grid justify-items-start gap-2">
          <Button
            type="button"
            color="brand"
            size="medium"
            className="rounded border-0"
            onClick={onTryAgain}
          >
            {t("value-check:try-again", "Try again")}
          </Button>
          <LinkButton
            href={createAdHref}
            color="brand"
            appearance="minimal"
            size="medium"
            className="px-0 underline"
            data-testid="value-check.create-ad-without-check"
          >
            {withoutCheck}
          </LinkButton>
        </div>
      )}
    </section>
  );
};
